/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { useParams } from '@tanstack/react-router'
import {
  useGetStudentDetail,
  useGetStudentStat,
} from '@/services/studentService'
import { useGetTuTorDetail, useGetTutorStat } from '@/services/tutorService'
import StudentAvatar from '@/assets/svg/student_avatar.svg'
import TutorAvatar from '@/assets/svg/tutor_avatar.svg'
import { useClassStore } from '@/stores/useClassStore'
import { formatMinutes } from '@/utils/time'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import BreadScrum from '@/components/BreadScrum'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import MarkIcon from '@/components/svg/MarkIcon'
import TutorStudentTable from './components/TuTorStudentsTable'
import MarkTable from './components/mark-table'
import Sidebar from './components/sidebar'
import Statistic from './components/statistic'
import { StudentMarkTab, TutorTableTabs } from './constants'

const StudentDetail = () => {
  const { studentId } = useParams({ strict: false })
  const { selectedUserRole } = useClassStore()
  const { selectedClass: storedSelectedClass } = useClassStore()
  const [tab, setTab] = useState('0')
  const [tableTab, setTableTab] = useState(
    selectedUserRole === 'student'
      ? StudentMarkTab.practice_test
      : TutorTableTabs.student
  )

  const { studentDetail, isLoadingStudentDetail, refetchStudentDetail } =
    useGetStudentDetail({
      studentId: studentId ?? '',
      enable: selectedUserRole === 'student' && !!studentId,
    })
  const { studentStat, isLoadingStudentStat } = useGetStudentStat({
    classId: tab,
    studentId: studentId ?? '',
    enable: selectedUserRole === 'student' && !!Number(tab),
  })

  const { tutorDetail, isLoadingTutorDetail, refetchTutorDetail } =
    useGetTuTorDetail(studentId ?? '', selectedUserRole === 'tutor')
  const { tutorStat, refetchTutorStat } = useGetTutorStat({
    classId: tab,
    tutorId: studentId ?? '',
    enable: selectedUserRole === 'tutor' && !!Number(tab),
  })

  const statData = useMemo(() => {
    const stutentData = [
      {
        title: 'Practice Duration',
        value: formatMinutes(studentStat?.practice_duration ?? 0),
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Questions Answered',
        value: studentStat?.questions_answered ?? 0,
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Average Accuracy',
        value: studentStat?.avg_accuracy ?? 'N/A',
        icon: <MarkIcon size={48} />,
        valueSuffix:
          studentStat?.avg_accuracy === null ||
          isLoadingStudentDetail ||
          isLoadingStudentStat
            ? ''
            : '%',
      },
    ]

    const tutorData = [
      {
        title: 'Students',
        value: tutorStat?.total_students ?? 0,
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Marked Practice Test',
        value: tutorStat?.marked_pt ?? 0,
        icon: <MarkIcon size={48} />,
      },
      {
        title: 'Marked Mock Exam',
        value: tutorStat?.marked_me ?? 0,
        icon: <MarkIcon size={48} />,
      },
    ]

    return selectedUserRole === 'student' ? stutentData : tutorData
  }, [studentStat, tutorStat])

  const userSideData = useMemo(() => {
    if (!tutorDetail && !studentDetail) {
      return { name: '', classes: [], email: '', accountType: 'MANUAL' }
    }

    const isTutor = selectedUserRole === 'tutor'

    const data = {
      name: isTutor ? tutorDetail?.tutor_name : studentDetail?.student_name,
      classes: isTutor ? tutorDetail?.classes : studentDetail?.list_of_classes,
      email: isTutor ? tutorDetail?.email : studentDetail?.email,
      accountType: isTutor
        ? tutorDetail?.created_type
        : studentDetail?.created_type,
    }

    setTab(data.classes?.[0]?.class_id.toString() ?? '0')

    return data
  }, [tutorDetail, studentDetail])

  useEffect(() => {
    if (
      (selectedUserRole === 'tutor' && !tutorDetail) ||
      (selectedUserRole === 'student' && !studentDetail)
    ) {
      setTab('0')
      return
    }

    setTab(
      selectedUserRole === 'tutor'
        ? (tutorDetail?.classes?.[0]?.class_id?.toString() ?? '0')
        : (studentDetail?.list_of_classes?.[0]?.class_id?.toString() ?? '0')
    )
  }, [studentDetail, tutorDetail])

  useEffect(() => {
    if (storedSelectedClass) {
      setTab(storedSelectedClass.class_id.toString())
    }
  }, [storedSelectedClass, studentDetail])

  const selectedClass = useMemo(() => {
    const selected = userSideData.classes?.find(
      (item) => item.class_id === Number(tab)
    )

    return selected
  }, [tab])

  return (
    <Main className='space-y-4 pt-4 pb-4'>
      <BreadScrum
        breadcrumbs={[
          { link: '/users/', label: 'User management' },
          { link: '', label: selectedUserRole },
          { link: '', label: userSideData.name ?? '' },
        ]}
      />
      <PageTitle title='User Details' />
      {isLoadingStudentDetail || isLoadingTutorDetail ? (
        <div className='grid h-[300px] grid-cols-3 gap-4'>
          <Skeleton className='h-full w-full bg-[#d6d6d6]' />
          <Skeleton className='col-span-2 h-full w-full bg-[#d6d6d6]' />
        </div>
      ) : (
        <Flex direction='row' gap='4' justify='between' className='w-full'>
          <Sidebar
            accountType={userSideData.accountType ?? 'MANUAL'}
            isActive={
              (selectedUserRole === 'tutor' ? tutorDetail : studentDetail)
                ?.is_active ?? true
            }
            refreshData={() => {
              if (selectedUserRole === 'tutor') {
                refetchTutorDetail()
                refetchTutorStat()
                return
              }
              // refetchStudentStat()
              refetchStudentDetail()
            }}
            avatar={
              selectedUserRole === 'student' ? StudentAvatar : TutorAvatar
            }
            setTab={setTab}
            tab={tab}
            name={userSideData?.name ?? ''}
            email={userSideData?.email ?? ''}
            classes={userSideData?.classes ?? []}
          />
          <div className='w-full space-y-4'>
            <Tabs value={tab} onValueChange={setTab} className='w-full'>
              <TabsContent value={tab}>
                <div className='space-y-4'>
                  <Statistic
                    data={statData}
                    isLoading={isLoadingStudentDetail || isLoadingTutorDetail}
                  />
                  {selectedUserRole === 'student' && (
                    <MarkTable
                      moduleId={
                        selectedClass
                          ? selectedClass?.module_id.toString()
                          : '0'
                      }
                      tableTab={tableTab}
                      setTableTab={setTableTab}
                      classId={tab}
                      studentId={studentId ?? ''}
                    />
                  )}

                  {selectedUserRole === 'tutor' && (
                    <TutorStudentTable
                      tableTab={tableTab}
                      setTableTab={setTableTab}
                      classId={tab}
                    />
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </Flex>
      )}
    </Main>
  )
}

export default StudentDetail
